// Copyright (c) 2025 Goffy. All Rights Reserved. This file belongs to Goffy.

import { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { markMysteryAcknowledged } from '@/services/mysterySetup';
import { getCurrentStartHole, setCurrentHole, setCurrentRoundId } from '@/services/currentRound';
import { resolveEntryHole } from '@/services/roundNavigation';
import { setCurrentTourId } from '@/services/currentTour';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';
import { getSubgameDisplayName } from '@/features/game-definitions/i18n';
import appColors, { alpha } from '@/theme/colors';

type Player = {
  id: string;
  name: string;
};

type Subgame = {
  id: string;
  name: string;
  description?: string;
};

type Hole = {
  number: number;
  par?: number;
  strokeindex?: number;
};

type RoundMeta = {
  holesCount?: number;
  HolesCount?: number;
};

type RoundSubgameEntry = {
  playerId: string;
  holeNumber: number;
  subgameId: string;
};

type SelectionState = {
  holeNumber?: number;
  subgameId?: string;
};

export default function MysteryPrizeScreen() {
  const { roundId: roundIdParam, tourId, teamGame: teamGameParam, debug: debugParam, returnHole: returnHoleParam } = useLocalSearchParams<{
    roundId?: string;
    tourId?: string;
    teamGame?: string;
    debug?: string;
    returnHole?: string;
  }>();
  const storedRoundId = useRoundState((state) => state.roundId);
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundId = roundIdFromParams ?? storedRoundId;
  const teamGame = teamGameParam === '1' || teamGameParam === 'true';
  const debug = debugParam === 'true';
  const returnHole = useMemo(() => {
    const raw = Array.isArray(returnHoleParam) ? returnHoleParam[0] : returnHoleParam;
    const parsed = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [returnHoleParam]);

  const [players, setPlayers] = useState<Player[]>([]);
  const [holes, setHoles] = useState<Hole[]>([]);
  const [subgames, setSubgames] = useState<Subgame[]>([]);
  const [selections, setSelections] = useState<Record<string, SelectionState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'hole' | 'subgame' | null>(null);
  const [pickerPlayerId, setPickerPlayerId] = useState<string | null>(null);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (roundIdFromParams && roundIdFromParams !== storedRoundId) {
      setCurrentRoundId(roundIdFromParams);
    }
  }, [roundIdFromParams, storedRoundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    if (!isNavigationReady || !roundId) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : undefined;
        const [playersRes, roundRes, subgamesRes, selectionsRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers }),
          fetch(`${apiBaseUrl}/api/subgames`, { headers }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, { headers }),
        ]);

        if (!playersRes.ok || !roundRes.ok || !subgamesRes.ok || !selectionsRes.ok) {
          throw new Error(
            `${t('mysteryPrize.loadError')} ` +
            `(players=${playersRes.status}, round=${roundRes.status}, subgames=${subgamesRes.status}, selections=${selectionsRes.status})`
          );
        }

        const playersPayload = (await playersRes.json()) as { players?: Player[]; Players?: Player[] };
        const roundPayload = (await roundRes.json()) as RoundMeta;
        const subgamesPayload = (await subgamesRes.json()) as Subgame[];
        const selectionsPayload = (await selectionsRes.json()) as RoundSubgameEntry[];
        const holesCount = Number(roundPayload.holesCount ?? roundPayload.HolesCount ?? 0);
        const derivedHoles = Number.isFinite(holesCount) && holesCount > 0
          ? Array.from({ length: holesCount }, (_, index) => ({ number: index + 1 }))
          : [];

        const nextSelections: Record<string, SelectionState> = {};
        selectionsPayload.forEach((entry) => {
          nextSelections[entry.playerId] = {
            holeNumber: entry.holeNumber,
            subgameId: entry.subgameId,
          };
        });

        setPlayers(playersPayload.players ?? playersPayload.Players ?? []);
        setHoles(derivedHoles);
        setSubgames(subgamesPayload ?? []);
        setSelections(nextSelections);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('mysteryPrize.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);

  if (!user) {
    return <Redirect href="/" />;
  }

  if (!roundId) {
    return (
      <View style={styles.errorPage}>
        <Text style={styles.errorText}>{t('mysteryPrize.loadError')}</Text>
      </View>
    );
  }

  const openPicker = (playerId: string, mode: 'hole' | 'subgame') => {
    setPickerPlayerId(playerId);
    setPickerMode(mode);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerMode(null);
    setPickerPlayerId(null);
  };

  const updateSelection = (playerId: string, next: SelectionState) => {
    setSelections((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        ...next,
      },
    }));
  };

  // Mystery setup is optional. `save` sends whatever complete selections exist (an empty set is a
  // valid "skip"). Either way we record that the user has handled mystery for this round so the
  // redirect guards on the hole/round-start screens don't bounce them back here.
  const proceed = async (save: boolean) => {
    if (!roundId) return;
    setSaving(true);
    if (save) {
      const payloadSelections = players
        .map((player) => ({
          playerId: player.id,
          holeNumber: selections[player.id]?.holeNumber,
          subgameId: selections[player.id]?.subgameId,
        }))
        .filter((entry) => entry.holeNumber && entry.subgameId);
      if (payloadSelections.length > 0) {
        const socket = getSharedBffWebSocket(apiBaseUrl);
        socket.setAuthToken(user?.token ?? null);
        socket.send({
          type: 'round.subgames.update',
          roundId,
          selections: payloadSelections.map(({ playerId, holeNumber, subgameId }) => ({
            playerId,
            holeNumber,
            subgameId,
          })),
        });
      }
    }

    // Persist the acknowledgement before navigating so the destination guard reads it and does not
    // redirect back (the subgame write above is published asynchronously and may not have landed yet).
    await markMysteryAcknowledged(roundId);

    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (returnHole) {
      // Opened mid-round from a hole page: return to that hole instead of restarting at hole 1.
      setCurrentHole(returnHole);
      if (tourId) params.push(`tourId=${tourId}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/hole/${returnHole}/compact${query}`);
    } else if (teamGame) {
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/roundstart${query}`);
    } else {
      // A round set up to start past hole 1 (back-nine / shotgun start) opens on that hole.
      const entryHole = resolveEntryHole(getCurrentStartHole());
      setCurrentHole(entryHole);
      if (tourId) params.push(`tourId=${tourId}`);
      const query = params.length ? `?${params.join('&')}` : '';
      router.replace(`/round/${roundId}/hole/${entryHole}/compact${query}`);
    }
  };

  const holeOptions = holes
    .map((hole) => hole.number)
    .filter((num) => Number.isFinite(num))
    .sort((a, b) => a - b);
  // A hole can host at most one mystery prize. Map each chosen hole to the player who owns it so
  // the hole picker can disable holes already taken by *other* players.
  const holeOwners = new Map<number, string>();
  Object.entries(selections).forEach(([playerId, selection]) => {
    if (selection?.holeNumber) {
      holeOwners.set(selection.holeNumber, playerId);
    }
  });
  const subgameById = subgames.reduce<Record<string, Subgame>>((acc, subgame) => {
    acc[subgame.id] = subgame;
    return acc;
  }, {});

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>{t('mysteryPrize.title')}</Text>
      <Text style={styles.subtitle}>{t('mysteryPrize.subtitle')}</Text>
      {loading ? <Text style={styles.meta}>{t('mysteryPrize.loading')}</Text> : null}
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
      {!loading && !error && players.length === 0 ? <Text style={styles.meta}>{t('mysteryPrize.noPlayers')}</Text> : null}
      {!loading && !error && subgames.length === 0 ? <Text style={styles.meta}>{t('mysteryPrize.noSubgames')}</Text> : null}
      <View style={styles.list}>
        {players.map((player) => {
          const selection = selections[player.id] ?? {};
          const holeLabel = selection.holeNumber ? `#${selection.holeNumber}` : t('mysteryPrize.selectHole');
          const subgameLabel = selection.subgameId
            ? getSubgameDisplayName(subgameById[selection.subgameId], t('mysteryPrize.selectSubgame'))
            : t('mysteryPrize.selectSubgame');
          return (
            <View key={player.id} style={styles.playerRow}>
              <Text style={styles.playerName}>{player.name}</Text>
              <View style={styles.choiceRow}>
                <Pressable style={styles.choiceButton} onPress={() => openPicker(player.id, 'hole')}>
                  <Text style={styles.choiceLabel}>{t('mysteryPrize.holeLabel')}</Text>
                  <Text style={styles.choiceValue}>{holeLabel}</Text>
                </Pressable>
                <Pressable style={styles.choiceButton} onPress={() => openPicker(player.id, 'subgame')}>
                  <Text style={styles.choiceLabel}>{t('mysteryPrize.subgameLabel')}</Text>
                  <Text style={styles.choiceValue}>{subgameLabel}</Text>
                </Pressable>
              </View>
            </View>
          );
        })}
      </View>
      <Pressable style={[styles.saveButton, saving && styles.saveButtonDisabled]} onPress={() => void proceed(true)} disabled={saving}>
        <Text style={styles.saveButtonText}>{saving ? t('mysteryPrize.saving') : t('mysteryPrize.save')}</Text>
      </Pressable>
      <Pressable style={styles.skipButton} onPress={() => void proceed(false)} disabled={saving}>
        <Text style={styles.skipButtonText}>{t('mysteryPrize.skip')}</Text>
      </Pressable>

      <Modal visible={pickerOpen} transparent animationType="fade" onRequestClose={closePicker}>
        <Pressable style={styles.modalBackdrop} onPress={closePicker}>
          <Pressable style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {pickerMode === 'hole' ? t('mysteryPrize.holePickerTitle') : t('mysteryPrize.subgamePickerTitle')}
            </Text>
            <ScrollView>
              {(pickerMode === 'hole' ? holeOptions : subgames).map((option) => {
                if (pickerMode === 'hole') {
                  const holeNumber = option as number;
                  const owner = holeOwners.get(holeNumber);
                  const takenByOther = owner != null && owner !== pickerPlayerId;
                  return (
                    <Pressable
                      key={`hole-${holeNumber}`}
                      style={[styles.modalOption, takenByOther && styles.modalOptionDisabled]}
                      disabled={takenByOther}
                      onPress={() => {
                        if (pickerPlayerId) {
                          updateSelection(pickerPlayerId, { holeNumber });
                        }
                        closePicker();
                      }}
                    >
                      <Text style={[styles.modalOptionText, takenByOther && styles.modalOptionTextDisabled]}>
                        {takenByOther ? `#${holeNumber} · ${t('mysteryPrize.holeTaken')}` : `#${holeNumber}`}
                      </Text>
                    </Pressable>
                  );
                }

                const subgame = option as Subgame;
                return (
                  <Pressable
                    key={subgame.id}
                    style={styles.modalOption}
                    onPress={() => {
                      if (pickerPlayerId) {
                        updateSelection(pickerPlayerId, { subgameId: subgame.id });
                      }
                      closePicker();
                    }}
                  >
                    <Text style={styles.modalOptionText}>{getSubgameDisplayName(subgame, t('mysteryPrize.selectSubgame'))}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 20,
    gap: 14,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 14,
    color: appColors.text.primary,
  },
  meta: {
    color: appColors.text.primary,
    fontSize: 13,
  },
  errorText: {
    color: appColors.status.errorTextSoft,
    fontSize: 13,
  },
  list: {
    gap: 12,
  },
  playerRow: {
    backgroundColor: alpha(appColors.surface.input, 0.78),
    borderRadius: 16,
    padding: 14,
    gap: 10,
  },
  playerName: {
    fontSize: 16,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  choiceRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  choiceButton: {
    flexGrow: 1,
    minWidth: 140,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  choiceLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: appColors.text.muted,
    marginBottom: 6,
  },
  choiceValue: {
    fontSize: 15,
    color: appColors.text.primary,
    fontWeight: '600',
  },
  saveButton: {
    paddingVertical: 14,
    borderRadius: 20,
    backgroundColor: appColors.background.interactive,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  skipButton: {
    marginTop: 10,
    paddingVertical: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
  },
  skipButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxHeight: '70%',
    backgroundColor: appColors.background.app,
    borderRadius: 20,
    padding: 16,
    gap: 10,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  modalOption: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.hairline,
  },
  modalOptionText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  modalOptionDisabled: {
    opacity: 0.4,
  },
  modalOptionTextDisabled: {
    color: appColors.text.muted,
  },
  errorPage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: appColors.background.app,
  },
});
